import {
  Button,
  Card,
  TextInputField,
  Banner,
  Primary13Regular,
  GreyWhite,
  Primary14SemiBold,
  GreyGrey60,
  IconKey,
  IconEyeOpen,
  IconEyeSlash,
  Tooltip,
  Box,
  Flex,
  Link
} from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { required, Validator } from '@sequencehq/utils/validation'
import Spinner from 'components/Loading/Spinner'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useMutation } from '@sequencehq/api/utils'
import { useState, useCallback } from 'react'
import { isProductionEnv } from 'lib/environment/environment'

export const rilletApiKeyValidation: Validator = (value: unknown) => {
  if (!value || typeof value !== 'string') {
    return 'API key is required'
  }
  if (/^(sandbox|live)_\*+$/.test(value)) {
    return 'Please enter a new API key to update credentials'
  }

  if (isProductionEnv()) {
    if (!value.startsWith('live_')) {
      return 'API key must start with live_'
    }
  } else {
    if (!value.startsWith('sandbox_')) {
      return 'API key must start with sandbox_'
    }
  }

  return undefined
}

export const rilletWebhookSecretValidation: Validator = (value: unknown) => {
  // Webhook secret is optional
  if (!!value && typeof value !== 'string') {
    return 'Webhook secret must be a string'
  }
  return undefined
}

const keyPrefix = isProductionEnv() ? 'live_' : 'sandbox_'

const rilletDashboardUrl = isProductionEnv()
  ? 'https://app.rillet.io/settings/api-access'
  : 'https://sandbox.rillet.io/settings/api-access'

const rilletWebhooksUrl = isProductionEnv()
  ? 'https://app.rillet.io/settings/webhooks'
  : 'https://sandbox.rillet.io/settings/webhooks'

type FormValues = {
  apiKey: string | undefined
  webhookSecret: string | undefined
}

interface Props {
  existingCredentials: FormValues
  onConnectionSuccess: (credentials: {
    apiKey: string
    webhookSecret?: string
  }) => void
  onCredentialsChange: (credentials: {
    apiKey?: string
    webhookSecret?: string
  }) => void
  hasExistingApiKey: boolean
}

const RilletAuthenticationCard = ({
  existingCredentials,
  onConnectionSuccess,
  onCredentialsChange,
  hasExistingApiKey
}: Props) => {
  const [showApiKey, setShowApiKey] = useState(false)
  const [showWebhookSecret, setShowWebhookSecret] = useState(false)
  const [apiTestResult, setApiTestResult] = useState<{
    status: 'success' | 'error' | null
    message?: string
  }>({ status: null })

  // Webhook field is disabled if no API key was previously saved
  // User must save the API key first before configuring webhook secrets
  const isWebhookSecretDisabled = !hasExistingApiKey

  const { fields, queries } = useForm({
    value: existingCredentials,
    fieldConfiguration: [
      {
        property: 'apiKey',
        validation: [required, rilletApiKeyValidation]
      },
      {
        property: 'webhookSecret',
        validation: [rilletWebhookSecretValidation]
      }
    ],
    onChange: onCredentialsChange
  })

  const {
    isPending: isTestingCredentials,
    reset: resetTestCredentials,
    mutate: testCredentials
  } = useMutation(dashboardv99990101Client.postTestRilletCredentials, {
    onSuccess: result => {
      if (result?.isValid && fields.apiKey.value) {
        setApiTestResult({
          status: 'success',
          message:
            'Credentials verified! Your Rillet API key has been validated.'
        })
        onConnectionSuccess({
          apiKey: fields.apiKey.value,
          webhookSecret: fields.webhookSecret.value
        })
        return
      }

      setApiTestResult({
        status: 'error',
        message: 'Invalid credentials. Please check your API key and try again.'
      })
    },
    onError: () => {
      setApiTestResult({
        status: 'error',
        message: 'Something went wrong, please try again.'
      })
    }
  })

  const handleApiKeyChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      resetTestCredentials()
      setApiTestResult({ status: null })
      fields.apiKey.onChange(event?.target?.value.trim())
    },
    [resetTestCredentials, fields.apiKey]
  )

  const handleTestCredentials = useCallback(() => {
    if (fields.apiKey.value) {
      void testCredentials({
        apiKey: fields.apiKey.value
      })
    }
  }, [fields.apiKey.value, testCredentials])

  const toggleShowApiKey = useCallback(() => {
    setShowApiKey(prev => !prev)
  }, [])

  const handleWebhookSecretChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      fields.webhookSecret.onChange(event?.target?.value.trim())
    },
    [fields.webhookSecret]
  )

  const toggleShowWebhookSecret = useCallback(() => {
    setShowWebhookSecret(prev => !prev)
  }, [])

  return (
    <Card>
      <Flex flexDirection="column" gap={6} data-testid="rilletAuth.card">
        <Box {...Primary14SemiBold}>Rillet authentication</Box>

        <Box>
          <Flex gap={3} alignItems="flex-end">
            <Box flex={1} minWidth={showApiKey ? '550px' : '450px'}>
              <TextInputField
                label="API Key"
                data-testid="rilletAuth.apiKey"
                value={fields.apiKey.value}
                onChange={handleApiKeyChange}
                validationErrors={fields.apiKey.validationErrors}
                placeholder={`${keyPrefix}${'x'.repeat(60)}`}
                type={showApiKey ? 'text' : 'password'}
                prefix={<IconKey color={GreyGrey60} />}
                fullWidth
                autoComplete="off"
                disabled={isTestingCredentials}
                suffix={
                  <Tooltip
                    label={showApiKey ? 'Hide API key' : 'Show API key'}
                    placement="top"
                    shouldWrapChildren={false}
                  >
                    <Button
                      aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                      icon={showApiKey ? <IconEyeSlash /> : <IconEyeOpen />}
                      variant="unstyled"
                      size="16px"
                      onClick={toggleShowApiKey}
                    />
                  </Tooltip>
                }
                suffixStyles={{ pointerEvents: 'auto' }}
              />
            </Box>

            <Box minWidth="140px">
              <Button
                data-testid="rilletAuth.testCredentials"
                onClick={handleTestCredentials}
                disabled={isTestingCredentials || !queries.isValid}
                leadingIcon={
                  isTestingCredentials ? (
                    <Spinner color={GreyWhite} height="16px" width="16px" />
                  ) : undefined
                }
                variant="secondary"
              >
                {isTestingCredentials ? 'Checking...' : 'Test credentials'}
              </Button>
            </Box>
          </Flex>

          <Box mt={2} fontSize="13px" color={GreyGrey60}>
            Enter your Rillet API key (starting with <code>{keyPrefix}</code> )
            to connect your account. You can find this in your{' '}
            <Link
              to={rilletDashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              color="blue.500"
              textDecoration="underline"
              isExternal
            >
              Rillet dashboard
            </Link>
            .
          </Box>
        </Box>

        <Box>
          <Flex gap={3} alignItems="flex-end">
            <Box flex={1} minWidth={showWebhookSecret ? '550px' : '450px'}>
              <TextInputField
                label="Webhook Secret"
                data-testid="rilletAuth.webhookSecret"
                value={fields.webhookSecret.value}
                onChange={handleWebhookSecretChange}
                validationErrors={fields.webhookSecret.validationErrors}
                placeholder={'*'.repeat(60)}
                type={showWebhookSecret ? 'text' : 'password'}
                prefix={<IconKey color={GreyGrey60} />}
                fullWidth
                autoComplete="off"
                disabled={isTestingCredentials || isWebhookSecretDisabled}
                suffix={
                  <Tooltip
                    label={
                      showWebhookSecret
                        ? 'Hide webhook secret'
                        : 'Show webhook secret'
                    }
                    placement="top"
                    shouldWrapChildren={false}
                  >
                    <Button
                      aria-label={
                        showWebhookSecret
                          ? 'Hide webhook secret'
                          : 'Show webhook secret'
                      }
                      icon={
                        showWebhookSecret ? <IconEyeSlash /> : <IconEyeOpen />
                      }
                      variant="unstyled"
                      size="16px"
                      onClick={toggleShowWebhookSecret}
                    />
                  </Tooltip>
                }
                suffixStyles={{ pointerEvents: 'auto' }}
              />
            </Box>
          </Flex>

          <Box mt={2} {...Primary13Regular} color={GreyGrey60}>
            {isWebhookSecretDisabled ? (
              <>
                Please test and save your API key first before configuring
                webhook secrets.
              </>
            ) : (
              <>
                Enter your Rillet webhook secret to sync invoice status updates
                from Rillet to Sequence. You can find this in your{' '}
                <Link
                  to={rilletWebhooksUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="blue.500"
                  textDecoration="underline"
                  isExternal
                >
                  Rillet dashboard
                </Link>
                .
              </>
            )}
          </Box>
        </Box>

        {apiTestResult.status === 'success' && (
          <Banner data-testid="rilletAuth.banner.success" variant="success">
            {apiTestResult.message}
          </Banner>
        )}

        {apiTestResult.status === 'error' && (
          <Banner data-testid="rilletAuth.banner.error" variant="error">
            {apiTestResult.message}
          </Banner>
        )}
      </Flex>
    </Card>
  )
}

export default RilletAuthenticationCard
